// Pure DOM helpers for InlineEditor.
//
// Extracted so they're testable in jsdom without rendering the React component
// (which depends on contentEditable + window.getSelection, neither of which
// jsdom implements faithfully). The React component imports from here.

import { FilterApplication, InlineNode } from '@/types';

export const VAR_PATH_ATTR = 'data-var-path';
export const VAR_FILTERS_ATTR = 'data-var-filters';

// Zero-width space character used to guarantee a caret-landable text node on
// each side of every chip. Without these, Chrome and Safari can put the caret
// in a position adjacent to a `contentEditable=false` chip that no longer
// accepts typed input — most visibly when a chip is the first child of the
// editor. We insert ZWSPs during render and strip them when reading the DOM
// back to the model.
export const ZWSP = '​';

// CSS class name resolution is tricky because the consuming component imports
// styles from a CSS module. We accept the class as a parameter so the helper
// remains pure / framework-agnostic for tests.
export interface DomHelperOptions {
  chipClassName?: string;
}

export function appendNodes(
  container: HTMLElement,
  nodes: InlineNode[],
  opts: DomHelperOptions = {}
) {
  for (const n of nodes) {
    if (n.type === 'text') {
      container.appendChild(document.createTextNode(n.value));
    } else {
      container.appendChild(makeChipElement(n.path, labelForPath(n.path), n.filters, opts));
    }
  }
  ensureChipCaretSafety(container);
}

export function makeChipElement(
  path: string,
  label: string,
  filters: FilterApplication[],
  opts: DomHelperOptions = {}
): HTMLElement {
  const chip = document.createElement('span');
  chip.setAttribute(VAR_PATH_ATTR, path);
  if (filters.length > 0) {
    chip.setAttribute(VAR_FILTERS_ATTR, JSON.stringify(filters));
  }
  chip.contentEditable = 'false';
  chip.className = opts.chipClassName ?? 'varChip';
  chip.textContent = chipLabelWithFilters(label, filters);
  return chip;
}

// Ensure every chip has a text-node neighbour on both sides so the caret can
// always land before / after it. Idempotent — safe to call after any DOM
// mutation that adds chips.
export function ensureChipCaretSafety(container: HTMLElement) {
  const chips = Array.from(container.querySelectorAll<HTMLElement>(`[${VAR_PATH_ATTR}]`));
  for (const chip of chips) {
    if (chip.parentNode !== container) {
      // Defensive: skip nested chips. The current renderer doesn't produce
      // them, but contenteditable browsers can occasionally wrap things in
      // unexpected ways.
      continue;
    }
    if (chip.previousSibling?.nodeType !== Node.TEXT_NODE) {
      container.insertBefore(document.createTextNode(ZWSP), chip);
    }
    if (chip.nextSibling?.nodeType !== Node.TEXT_NODE) {
      container.insertBefore(document.createTextNode(ZWSP), chip.nextSibling);
    }
  }
}

export function chipLabelWithFilters(label: string, filters: FilterApplication[]): string {
  if (filters.length === 0) {
    return label;
  }
  return `${label} | ${filters.map((f) => f.name).join(' | ')}`;
}

export function labelForPath(path: string): string {
  // Crude humanisation for chips constructed from raw paths (e.g. on parse).
  // The picker also passes a `label` when it has one; this fallback keeps the
  // tree-of-truth canonical and lets us recover a reasonable display.
  const last = path.split('.').pop() ?? path;
  return last.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function filterToJinja(f: FilterApplication): string {
  if (f.args.length === 0) {
    return ` | ${f.name}`;
  }
  const args = f.args
    .map((a) => (a.startsWith('=') ? a.slice(1) : `'${a.replaceAll("'", String.raw`\'`)}'`))
    .join(', ');
  return ` | ${f.name}(${args})`;
}

export function domToNodes(container: HTMLElement): InlineNode[] {
  const out: InlineNode[] = [];
  container.childNodes.forEach((child) => {
    const node = domChildToInlineNode(child);
    if (node) {
      out.push(node);
    }
  });
  return coalesceAdjacentText(out);
}

function domChildToInlineNode(child: ChildNode): InlineNode | null {
  if (child.nodeType === Node.TEXT_NODE) {
    // Strip the caret-safety ZWSPs we inserted around chips. They're an
    // implementation detail and must not leak into the model (or into the
    // serialised Jinja).
    const value = (child.textContent ?? '').replaceAll(ZWSP, '');
    return value.length > 0 ? { type: 'text', value } : null;
  }
  if (child.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }
  const el = child as HTMLElement;
  const path = el.getAttribute(VAR_PATH_ATTR);
  if (path) {
    return { type: 'var', path, filters: parseChipFilters(el) };
  }
  if (el.tagName === 'BR') {
    // Treat <br> as a literal newline in the underlying text. The browser
    // inserts these when the user presses Enter in a paragraph.
    return { type: 'text', value: '\n' };
  }
  const value = (el.textContent ?? '').replaceAll(ZWSP, '');
  return value.length > 0 ? { type: 'text', value } : null;
}

function parseChipFilters(el: HTMLElement): FilterApplication[] {
  const filtersAttr = el.getAttribute(VAR_FILTERS_ATTR);
  if (!filtersAttr) {
    return [];
  }
  try {
    return JSON.parse(filtersAttr);
  } catch {
    return [];
  }
}

function coalesceAdjacentText(nodes: InlineNode[]): InlineNode[] {
  const out: InlineNode[] = [];
  for (const n of nodes) {
    const last = out.at(-1);
    if (n.type === 'text' && last?.type === 'text') {
      out[out.length - 1] = { type: 'text', value: last.value + n.value };
    } else {
      out.push(n);
    }
  }
  return out;
}
